/**
 * planningSchemas.js — Single source of truth for event-type planning forms.
 *
 * Schema-driven: every event type defines its own steps, fields, types,
 * validation, conditional logic, and mapping metadata.
 *
 * ADDING A NEW EVENT TYPE:
 *   1. Add a schema object below keyed by event type
 *   2. That's it — the renderer, mapping layer, and backend all read from here
 *
 * FIELD TYPES: text, email, phone, date, time, yes_no, dropdown, number,
 *              song, textarea, repeating_group, time_range
 *
 * CONDITIONAL: { field: 'someFieldId', value: 'yes' } — field only shows
 *              when the referenced field has the specified value
 *
 * MAP_TO: { target: 'planning'|'logistics'|'staffing'|'timeline'|'music', field: 'fieldName' }
 */

// ── Shared field builders ────────────────────────────────────────────────

const generalInfoFields = (eventLabel) => [
  { id: 'contactName', label: 'Primary Contact Name', type: 'text', required: true, critical: true, placeholder: 'Who should we coordinate with?' },
  { id: 'contactPhone', label: 'Contact Phone', type: 'phone', required: true, critical: true, placeholder: '(555) 123-4567' },
  { id: 'contactEmail', label: 'Contact Email', type: 'email', required: false, placeholder: 'email@example.com' },
  { id: 'guestOfHonor', label: `${eventLabel} Guest of Honor`, type: 'text', required: true, critical: true, placeholder: 'Full name', mapTo: { target: 'planning', field: 'guestOfHonor' } },
  { id: 'guestOfHonorAge', label: 'Age (if applicable)', type: 'number', required: false, placeholder: '' },
  { id: 'eventDate', label: 'Event Date', type: 'date', required: true, critical: true, prefillFrom: 'eventDate', mapTo: { target: 'planning', field: 'eventDate' } },
  { id: 'startTime', label: 'Event Start Time', type: 'time', required: true, critical: true, prefillFrom: 'startTime', mapTo: { target: 'logistics', field: 'eventStartTime' } },
  { id: 'endTime', label: 'Event End Time', type: 'time', required: true, prefillFrom: 'endTime', mapTo: { target: 'logistics', field: 'eventEndTime' } },
  { id: 'cocktailStartTime', label: 'Cocktail Start Time', type: 'time', required: false },
  { id: 'guestArrival', label: 'Guest Arrival', type: 'time', required: false, mapTo: { target: 'logistics', field: 'guestArrivalTime' } },
  { id: 'guestOfHonorArrival', label: 'Guest of Honor Arrival', type: 'time', required: false, mapTo: { target: 'logistics', field: 'guestOfHonorArrivalTime' } },
  { id: 'venueName', label: 'Venue Name', type: 'text', required: true, critical: true, prefillFrom: 'venue', placeholder: 'e.g. The Grand Ballroom', mapTo: { target: 'logistics', field: 'venueName' } },
  { id: 'venueAddress', label: 'Venue Address', type: 'text', required: false, placeholder: 'Full address', mapTo: { target: 'logistics', field: 'venueAddress' } },
  { id: 'venuePhone', label: 'Venue Phone', type: 'phone', required: false, placeholder: '(555) 000-0000', mapTo: { target: 'logistics', field: 'venuePhone' } },
  { id: 'roomName', label: 'Room Name', type: 'text', required: false, placeholder: 'e.g. Grand Ballroom A, Terrace Room', mapTo: { target: 'logistics', field: 'roomName' } },
  { id: 'roomFloor', label: 'What floor is the room on', type: 'text', required: false, placeholder: 'e.g. 2nd floor, Ground level', mapTo: { target: 'logistics', field: 'roomFloor' } },
  { id: 'elevator', label: 'Elevator Access', type: 'yes_no', required: false, mapTo: { target: 'logistics', field: 'elevatorAccess' } },
  { id: 'setting', label: 'Setting', type: 'dropdown', required: false, options: ['Indoors', 'Outdoors', 'Both'], mapTo: { target: 'logistics', field: 'setting' } },
  { id: 'totalGuests', label: 'Number of Guests', type: 'number', required: false, placeholder: 'Total expected', mapTo: { target: 'planning', field: 'totalGuests' } },
  { id: 'adultCount', label: 'How Many Adults', type: 'number', required: true, critical: true, placeholder: '0', mapTo: { target: 'planning', field: 'adultCount' } },
  { id: 'childCount', label: 'How Many Kids', type: 'number', required: false, placeholder: '0', mapTo: { target: 'planning', field: 'childCount' } },
  { id: 'photographer', label: 'Photographer', type: 'text', required: false, placeholder: 'Name or company', audience: ['planner'] },
  { id: 'videographer', label: 'Videographer', type: 'text', required: false, placeholder: 'Name or company', audience: ['planner'] },
  { id: 'eventColors', label: 'Event Colors', type: 'text', required: false, placeholder: 'e.g. Navy blue & gold, blush pink', mapTo: { target: 'planning', field: 'eventColors' } },
  { id: 'eventTheme', label: 'Event Theme', type: 'text', required: false, placeholder: 'e.g. Enchanted Garden, Sports, Hollywood', mapTo: { target: 'planning', field: 'eventTheme' } },
];

const otherInfoFields = () => [
  { id: 'musicStyle', label: 'Overall Music Style', type: 'textarea', required: false, placeholder: 'e.g. Top 40, R&B, classic hits, lounge during dinner...', mapTo: { target: 'music', field: 'musicStyle' } },
  { id: 'spotifyPlaylist', label: 'Submit Your Spotify Playlist Link Here', type: 'url', required: false, placeholder: 'https://open.spotify.com/playlist/...', mapTo: { target: 'music', field: 'spotifyPlaylist' } },
  { id: 'songRequestPage', label: 'Song Requests', type: 'song_picker', required: false, fullWidth: true },
  { id: 'specialRequests', label: 'Other Info', type: 'textarea', rows: 6, required: false, placeholder: 'Anything else — special requests, accessibility needs, extra details, or anything the form above did not cover.', mapTo: { target: 'planning', field: 'specialRequests' } },
];

const buildCandleFields = (count, label) => {
  const fields = [];
  for (let i = 1; i <= count; i++) {
    fields.push(
      { id: `candle${i}Honoree`, label: `Candle ${i} For`, type: 'text', required: false, placeholder: `Who lights candle ${i}?` },
      { id: `candle${i}Song`, label: `Song ${i}`, type: 'song', required: false, placeholder: 'Song title — Artist' },
    );
  }
  return fields;
};


// ══════════════════════════════════════════════════════════════════════════
//  SCHEMAS
// ══════════════════════════════════════════════════════════════════════════

const planningSchemas = {

  // ── MITZVAH ──────────────────────────────────────────────────────────
  mitzvah: {
    eventType: 'mitzvah',
    title: 'Mitzvah Planning Form',
    description: 'Help us create an unforgettable celebration. Please fill out each section so our team can prepare every detail.',
    steps: [
      {
        id: 'general',
        title: 'General Info',
        description: 'Basic event and contact details',
        fields: [
          ...generalInfoFields('Mitzvah'),
          { id: 'serviceTime', label: 'Service Start & End Time', type: 'text', required: false, placeholder: 'e.g. 10:00 AM – 12:00 PM' },
        ],
      },
      {
        id: 'highlights',
        title: 'Event Highlights',
        description: 'Key moments and traditions for the celebration',
        fields: [
          { id: 'cocktailMusic', label: 'Cocktail Music', type: 'yes_no', required: false, mapTo: { target: 'music', field: 'cocktailMusic' } },
          { id: 'cocktailMusicType', label: 'Cocktail Music Type', type: 'text', required: false, placeholder: 'e.g. Jazz, lounge, acoustic', conditional: { field: 'cocktailMusic', value: 'yes' }, mapTo: { target: 'music', field: 'cocktailMusicStyle' } },
          { id: 'dinnerMusic', label: 'Dinner Music', type: 'yes_no', required: false, mapTo: { target: 'music', field: 'dinnerMusic' } },
          { id: 'dinnerMusicType', label: 'Dinner Music Type', type: 'text', required: false, placeholder: 'e.g. Soft background, classic hits, instrumental', conditional: { field: 'dinnerMusic', value: 'yes' }, mapTo: { target: 'music', field: 'dinnerMusicStyle' } },
          { id: 'speech', label: 'Speech', type: 'yes_no', required: false },
          { id: 'speechBy', label: 'Speech By', type: 'text', required: false, placeholder: 'Who is giving the speech?', conditional: { field: 'speech', value: 'yes' }, mapTo: { target: 'timeline', field: 'speechBy' } },
          { id: 'toast', label: 'Toast', type: 'yes_no', required: false },
          { id: 'toastBy', label: 'Toast By', type: 'text', required: false, placeholder: 'Who is giving the toast?', conditional: { field: 'toast', value: 'yes' }, mapTo: { target: 'timeline', field: 'toastBy' } },
          { id: 'whenToToast', label: 'When to Toast', type: 'text', required: false, placeholder: 'e.g. Before dinner, after blessing', conditional: { field: 'toast', value: 'yes' }, mapTo: { target: 'timeline', field: 'toastTiming' } },
          { id: 'hamotzi', label: 'Hamotzi', type: 'yes_no', required: false },
          { id: 'hamotziBy', label: 'Hamotzi By', type: 'text', required: false, placeholder: 'Who performs the Hamotzi?', conditional: { field: 'hamotzi', value: 'yes' } },
          { id: 'kiddush', label: 'Kiddush', type: 'yes_no', required: false },
          { id: 'kiddushBy', label: 'Kiddush By', type: 'text', required: false, placeholder: 'Who performs the Kiddush?', conditional: { field: 'kiddush', value: 'yes' } },
          { id: 'hora', label: 'Hora', type: 'yes_no', required: false, mapTo: { target: 'timeline', field: 'hora' } },
          { id: 'horaDuration', label: 'Hora Duration', type: 'dropdown', required: false, options: ['Short & Sweet', 'Medium', 'Full Traditional'], conditional: { field: 'hora', value: 'yes' } },
          { id: 'horaWhoOnChair', label: "Who's Going On Chair", type: 'text', required: false, placeholder: 'e.g. Guest of honor, parents', conditional: { field: 'hora', value: 'yes' } },
          { id: 'horaStyle', label: 'Hora Style', type: 'dropdown', required: false, options: ['Traditional', 'Modern', 'Mix'], conditional: { field: 'hora', value: 'yes' } },
          { id: 'horaSong', label: 'Hora Song', type: 'song', required: false, placeholder: 'Hava Nagila or custom', conditional: { field: 'hora', value: 'yes' }, mapTo: { target: 'music', field: 'horaSong' } },
          { id: 'dinnerServed', label: 'Dinner Served', type: 'yes_no', required: false },
          { id: 'dinnerServedWhen', label: 'When is Dinner Served', type: 'text', required: false, placeholder: 'e.g. After cocktail hour, 7:00 PM', conditional: { field: 'dinnerServed', value: 'yes' }, mapTo: { target: 'timeline', field: 'dinnerTiming' } },
          { id: 'montage', label: 'Montage Viewing', type: 'yes_no', required: false },
          { id: 'montageTiming', label: 'Montage Viewing When', type: 'dropdown', required: false, options: ['Before Dinner', 'During Dinner', 'After Dinner', 'Before Dancing', 'During Candle Lighting'], conditional: { field: 'montage', value: 'yes' } },
          { id: 'honorDance', label: 'Honor Dance', type: 'yes_no', required: false },
          { id: 'honorDanceSong', label: 'Honor Dance Song', type: 'song', required: false, placeholder: 'Song title — Artist', conditional: { field: 'honorDance', value: 'yes' }, mapTo: { target: 'music', field: 'honorDanceSong' } },
          { id: 'lastDance', label: 'Last Dance', type: 'yes_no', required: false },
          { id: 'lastDanceSong', label: 'Last Song', type: 'song', required: false, placeholder: 'The final song of the night', conditional: { field: 'lastDance', value: 'yes' }, mapTo: { target: 'music', field: 'lastDanceSong' } },
          { id: 'games', label: 'Game During Party?', type: 'yes_no', required: false },
          { id: 'gamesDetails', label: 'Game Details', type: 'textarea', required: false, placeholder: 'Describe any games, contests, or interactive activities...', conditional: { field: 'games', value: 'yes' } },
          { id: 'dancersTshirts', label: 'Dancers T-Shirts?', type: 'yes_no', required: false },
          { id: 'dancersAttireColor', label: 'Dancers Attire Color', type: 'text', required: false, placeholder: 'e.g. Black, neon, match event colors', conditional: { field: 'dancersTshirts', value: 'yes' }, mapTo: { target: 'staffing', field: 'dancersAttireColor' } },
        ],
      },
      {
        id: 'introductions',
        title: 'Introductions',
        description: 'Grand entrance and family introductions',
        layout: 'paired',
        fields: [
          { id: 'parents', label: 'Parents', type: 'text', required: false, placeholder: 'e.g. Dana & Michael' },
          { id: 'parentsSongChoice', label: 'Parents Intro Song', type: 'song', required: false, placeholder: 'Song for parents intro', mapTo: { target: 'music', field: 'parentsIntroSong' } },
          {
            id: 'siblings',
            label: 'Siblings',
            type: 'repeater',
            required: false,
            fullWidth: true,
            addLabel: '+ Add sibling',
            emptyLabel: 'Add siblings who will be introduced',
            itemLabel: 'Sibling',
            minItems: 0,
            maxItems: 8,
            defaultItems: 1,
            itemFields: [
              { id: 'name', label: 'Brother / Sister', type: 'text', placeholder: 'Sibling name' },
              { id: 'song', label: 'Sibling Intro Song', type: 'song', placeholder: 'Song for sibling intro' },
            ],
          },
          { id: 'guestOfHonor1', label: 'Guest Of Honor 1', type: 'text', required: false, placeholder: 'Name of guest of honor', mapTo: { target: 'timeline', field: 'guestOfHonor1' } },
          { id: 'introSong', label: 'Intro Song', type: 'song', required: false, placeholder: 'Song for guest of honor entrance', mapTo: { target: 'music', field: 'entranceSong' } },
          { id: 'guestOfHonor2', label: 'Guest of Honor 2', type: 'text', required: false, placeholder: 'Name (if applicable)', mapTo: { target: 'timeline', field: 'guestOfHonor2' } },
          { id: 'introSong2', label: 'Intro Song', type: 'song', required: false, placeholder: 'Song for second guest of honor' },
          { id: 'escortedBy', label: 'Escorted by', type: 'dropdown', required: false, options: ['Both Parents', 'Mother', 'Father', 'Sibling', 'Grandparent', 'With Dancers', 'Other'], fullWidth: true },
          { id: 'introductionsOther', label: 'Other Information', type: 'textarea', required: false, placeholder: 'Anything else we should know about the introductions — order, surprise walk-ins, pronunciations, special requests...', fullWidth: true },
        ],
      },
      {
        id: 'candles',
        title: 'Candle Lighting',
        description: 'The 13 candle lighting ceremony',
        layout: 'paired',
        fields: [
          { id: 'candleLighting', label: 'Will there be a Candle Lighting Ceremony?', type: 'yes_no', required: true, fullWidth: true },
          { id: 'candleLightingSong', label: 'Background song during candle lighting', type: 'song', required: false, placeholder: 'Instrumental or soft background', conditional: { field: 'candleLighting', value: 'yes' }, fullWidth: true },
          ...buildCandleFields(13, 'Mitzvah').map(f => ({ ...f, conditional: { field: 'candleLighting', value: 'yes' } })),
        ],
      },
      {
        id: 'other',
        title: 'Other Information',
        description: 'Music preferences, announcements, and special requests',
        fields: otherInfoFields(),
      },
    ],
  },

  // ── WEDDING ──────────────────────────────────────────────────────────
  wedding: {
    eventType: 'wedding',
    title: 'Wedding Planning Form',
    description: 'Congratulations! Help us plan your perfect day by sharing the details below.',
    steps: [
      {
        id: 'general',
        title: 'General Info',
        description: 'Basic event and contact details',
        fields: [
          { id: 'brideName', label: "Bride's Name", type: 'text', required: true, critical: true, placeholder: 'Full name', mapTo: { target: 'planning', field: 'guestOfHonor' } },
          { id: 'groomName', label: "Groom's Name", type: 'text', required: true, critical: true, placeholder: 'Full name' },
          { id: 'brideCell', label: "Bride's Cell", type: 'phone', required: false, placeholder: '(555) 000-0000' },
          { id: 'groomCell', label: "Groom's Cell", type: 'phone', required: false, placeholder: '(555) 000-0000' },
          { id: 'brideEmail', label: "Bride's Email", type: 'email', required: false, placeholder: 'email@example.com' },
          { id: 'groomEmail', label: "Groom's Email", type: 'email', required: false, placeholder: 'email@example.com' },
          { id: 'eventDate', label: 'Wedding Date', type: 'date', required: true, critical: true, prefillFrom: 'eventDate', mapTo: { target: 'planning', field: 'eventDate' } },
          { id: 'receptionStartTime', label: 'Reception Start Time', type: 'time', required: true, prefillFrom: 'startTime', mapTo: { target: 'timeline', field: 'receptionTime' } },
          { id: 'receptionEndTime', label: 'Reception End Time', type: 'time', required: true, prefillFrom: 'endTime', mapTo: { target: 'logistics', field: 'eventEndTime' } },
          { id: 'banquetVenue', label: 'Banquet Venue', type: 'text', required: true, critical: true, prefillFrom: 'venue', placeholder: 'Venue name', mapTo: { target: 'logistics', field: 'venueName' } },
          { id: 'guestCount', label: 'Number of Guests', type: 'number', required: false, placeholder: '0', mapTo: { target: 'planning', field: 'adultCount' } },
          { id: 'roomName', label: 'Room Name', type: 'text', required: false, placeholder: 'e.g. Grand Ballroom A', mapTo: { target: 'logistics', field: 'roomName' } },
          { id: 'contactName', label: 'Contact Person', type: 'text', required: true, placeholder: 'Wedding planner or point person' },
          { id: 'guestArrival', label: 'Guest Arrival', type: 'time', required: false, mapTo: { target: 'logistics', field: 'guestArrivalTime' } },
          { id: 'bridalPartyArrival', label: 'Bridal Party Arrival', type: 'time', required: false, mapTo: { target: 'logistics', field: 'bridalPartyArrivalTime' } },
          { id: 'photographer', label: 'Photographer', type: 'text', required: false, placeholder: 'Name or company', audience: ['planner'] },
          { id: 'videographer', label: 'Videographer', type: 'text', required: false, placeholder: 'Name or company', audience: ['planner'] },
          { id: 'djAttire', label: 'DJ Attire', type: 'text', required: false, placeholder: 'e.g. All black, formal suit', mapTo: { target: 'staffing', field: 'attire' }, audience: ['dj'] },
          { id: 'roomFloor', label: 'What floor is the room on', type: 'text', required: false, placeholder: 'e.g. 2nd floor, Ground level', mapTo: { target: 'logistics', field: 'roomFloor' } },
          { id: 'setting', label: 'Setting', type: 'dropdown', required: false, options: ['Indoors', 'Outdoors', 'Both'], mapTo: { target: 'logistics', field: 'setting' } },
          { id: 'elevator', label: 'Elevator', type: 'yes_no', required: false, mapTo: { target: 'logistics', field: 'elevatorAccess' } },
          { id: 'eventColors', label: 'Colors', type: 'text', required: false, placeholder: 'e.g. Navy blue & gold, blush pink', mapTo: { target: 'planning', field: 'eventColors' } },
          { id: 'eventTheme', label: 'Theme', type: 'text', required: false, placeholder: 'e.g. Rustic, Modern, Classic', mapTo: { target: 'planning', field: 'eventTheme' } },
          { id: 'weddingStyle', label: 'Wedding Style', type: 'text', required: false, placeholder: 'e.g. Traditional, Contemporary, Destination' },
        ],
      },
      {
        id: 'ceremony',
        title: 'Ceremony',
        description: 'Music and details for the ceremony',
        fields: [
          { id: 'ceremonyMusic', label: 'Do you need dj/music for ceremony', type: 'yes_no', required: false },
          { id: 'ceremonyLocation', label: 'Ceremony Location', type: 'text', required: false, placeholder: 'e.g. Church, garden, same venue' },
          { id: 'ceremonyTime', label: 'Time of Ceremony', type: 'time', required: false, mapTo: { target: 'timeline', field: 'ceremonyTime' } },
          { id: 'ceremonyEquipment', label: 'Equipment needed for ceremony', type: 'text', required: false, placeholder: 'e.g. Wireless mic, speakers, lapel mic' },
          { id: 'preludeSong', label: 'Prelude Guest Music', type: 'song', required: false, placeholder: 'Soft background music as guests arrive', mapTo: { target: 'music', field: 'preludeSong' } },
          { id: 'bridesmaidsProcessional', label: 'Brides Maid Processional Song', type: 'song', required: false, placeholder: 'Song title — Artist', mapTo: { target: 'music', field: 'bridesmaidsProcessional' } },
          { id: 'brideProcessional', label: 'Bride Processional Song', type: 'song', required: false, placeholder: 'Song title — Artist', mapTo: { target: 'music', field: 'brideProcessional' } },
          { id: 'recessionalSong', label: 'Ceremony Recessional Song', type: 'song', required: false, placeholder: 'Celebratory exit song', mapTo: { target: 'music', field: 'recessionalSong' } },
          { id: 'unityCandleSand', label: 'Unity Candle / Sand Pouring', type: 'yes_no', required: false },
        ],
      },
      {
        id: 'cocktail',
        title: 'Cocktail Hour',
        description: 'Music and flow for cocktail hour',
        fields: [
          { id: 'cocktailHour', label: 'Do you need dj/music for Cocktail Hour', type: 'yes_no', required: false },
          { id: 'cocktailMusicStyle', label: 'If YES, style of Music', type: 'text', required: false, placeholder: 'e.g. Jazz, acoustic, lounge', conditional: { field: 'cocktailHour', value: 'yes' }, mapTo: { target: 'music', field: 'cocktailMusicStyle' } },
          { id: 'cocktailLocation', label: 'Location of Cocktail', type: 'text', required: false, placeholder: 'e.g. Terrace, lobby, separate room' },
          { id: 'cocktailTime', label: 'Time of Cocktail', type: 'time', required: false },
          { id: 'cocktailMusicHow', label: 'How will the Music be played', type: 'text', required: false, placeholder: 'e.g. Live DJ, Bluetooth speaker, venue system' },
        ],
      },
      {
        id: 'reception',
        title: 'Reception Highlights',
        description: 'The main event — entrances, dances, and key moments',
        fields: [
          { id: 'dinnerMusic', label: 'Dinner Music', type: 'yes_no', required: false, mapTo: { target: 'music', field: 'dinnerMusic' } },
          { id: 'dinnerMusicType', label: 'Dinner Music Type', type: 'text', required: false, placeholder: 'e.g. Soft background, classic hits', conditional: { field: 'dinnerMusic', value: 'yes' }, mapTo: { target: 'music', field: 'dinnerMusicStyle' } },
          { id: 'introductions', label: 'Introductions', type: 'yes_no', required: false },
          { id: 'introductionsWhen', label: 'When?', type: 'dropdown', required: false, options: ['Before Dinner', 'After Cocktail Hour', 'After First Dance', 'Other'], conditional: { field: 'introductions', value: 'yes' } },
          { id: 'dinnerServed', label: 'Dinner Served', type: 'yes_no', required: false },
          { id: 'dinnerStyle', label: 'Style', type: 'dropdown', required: false, options: ['Buffet', 'Plated', 'Family Style', 'Stations', 'Other'], conditional: { field: 'dinnerServed', value: 'yes' }, mapTo: { target: 'logistics', field: 'dinnerStyle' } },
          { id: 'cakeCutting', label: 'Cake Cutting', type: 'yes_no', required: false },
          { id: 'cakeCuttingWhen', label: 'When', type: 'dropdown', required: false, options: ['Before Dinner', 'After Dinner', 'After Dancing', 'Other'], conditional: { field: 'cakeCutting', value: 'yes' } },
          { id: 'cakeSong', label: 'Cake Cutting Song', type: 'song', required: false, placeholder: 'Song title — Artist', conditional: { field: 'cakeCutting', value: 'yes' }, mapTo: { target: 'music', field: 'cakeSong' } },
          { id: 'cakeFeedingSong', label: 'Cake Feeding Song', type: 'song', required: false, placeholder: 'Song title — Artist', conditional: { field: 'cakeCutting', value: 'yes' } },
          { id: 'bridalPartyDance', label: 'Bridal Party Dance', type: 'yes_no', required: false },
          { id: 'bridalPartySong', label: 'Bridal Party Song', type: 'song', required: false, placeholder: 'Song title — Artist', conditional: { field: 'bridalPartyDance', value: 'yes' } },
          { id: 'bouquetToss', label: 'Bouquet Toss', type: 'yes_no', required: false },
          { id: 'bouquetSong', label: 'Bouquet Song', type: 'song', required: false, placeholder: 'Song title — Artist', conditional: { field: 'bouquetToss', value: 'yes' }, mapTo: { target: 'music', field: 'bouquetSong' } },
          { id: 'garterPull', label: 'Garter Pull', type: 'yes_no', required: false },
          { id: 'garterPullSong', label: 'Pull Song', type: 'text', required: false, placeholder: 'Song title — Artist', conditional: { field: 'garterPull', value: 'yes' } },
          { id: 'garterToss', label: 'Garter Toss', type: 'yes_no', required: false },
          { id: 'garterTossSong', label: 'Garter Toss Song', type: 'song', required: false, placeholder: 'Song title — Artist', conditional: { field: 'garterToss', value: 'yes' } },
          { id: 'garterPutBack', label: 'Garter Put Back', type: 'text', required: false, placeholder: 'Any notes' },
          { id: 'garterPutBackSong', label: 'Put Back Song', type: 'song', required: false, placeholder: 'Song title — Artist' },
          { id: 'anniversaryDance', label: 'Anniversary Dance', type: 'yes_no', required: false },
          { id: 'anniversaryDanceSong', label: 'Song', type: 'text', required: false, placeholder: 'Song title — Artist', conditional: { field: 'anniversaryDance', value: 'yes' } },
          { id: 'centerPieceGiveaway', label: 'Center Piece Giveaway?', type: 'yes_no', required: false },
          { id: 'lastDance', label: 'Last Dance of Evening', type: 'yes_no', required: false },
          { id: 'lastDanceSong', label: 'Last Dance Song', type: 'song', required: false, placeholder: 'The final song of the night', conditional: { field: 'lastDance', value: 'yes' }, mapTo: { target: 'music', field: 'lastDanceSong' } },
          { id: 'specialDances', label: 'Other Special Dances', type: 'yes_no', required: false },
          { id: 'specialDancesList', label: 'List Special Dances', type: 'song', required: false, placeholder: 'Describe any other dances...', conditional: { field: 'specialDances', value: 'yes' } },
        ],
      },
      {
        id: 'introductions',
        title: 'Wedding Party Introductions',
        description: 'Reception introductions for wedding party and family',
        fields: [
          { id: 'bridalPartyIntroSong', label: 'Bridal Party Intro Song', type: 'song', required: false, placeholder: 'Upbeat song for the party entrance', mapTo: { target: 'music', field: 'introSong' } },
          { id: 'flowerGirlRingbearer', label: 'Flower Girl(s) & Ringbearer', type: 'text', required: false, placeholder: 'Names' },
          { id: 'bridesmaidGroomsman1', label: 'Bridesmaid & Groomsman (1)', type: 'text', required: false, placeholder: 'Names' },
          { id: 'bridesmaidGroomsman2', label: 'Bridesmaid & Groomsman (2)', type: 'text', required: false, placeholder: 'Names' },
          { id: 'bridesmaidGroomsman3', label: 'Bridesmaid & Groomsman (3)', type: 'text', required: false, placeholder: 'Names' },
          { id: 'bridesmaidGroomsman4', label: 'Bridesmaid & Groomsman (4)', type: 'text', required: false, placeholder: 'Names' },
          { id: 'bridesmaidGroomsman5', label: 'Bridesmaid & Groomsman (5)', type: 'text', required: false, placeholder: 'Names' },
          { id: 'bridesmaidGroomsman6', label: 'Bridesmaid & Groomsman (6)', type: 'text', required: false, placeholder: 'Names' },
          { id: 'bridesmaidGroomsman7', label: 'Bridesmaid & Groomsman (7)', type: 'text', required: false, placeholder: 'Names' },
          { id: 'bridesmaidGroomsman8', label: 'Bridesmaid & Groomsman (8)', type: 'text', required: false, placeholder: 'Names' },
          { id: 'bridesmaidGroomsman9', label: 'Bridesmaid & Groomsman (9)', type: 'text', required: false, placeholder: 'Names' },
          { id: 'bridesmaidGroomsman10', label: 'Bridesmaid & Groomsman (10)', type: 'text', required: false, placeholder: 'Names' },
          { id: 'maidOfHonorBestMan', label: 'Maid/Matron of Honor & Best Man', type: 'text', required: false, placeholder: 'Names' },
          { id: 'brideGroomIntroSong', label: 'Bride & Groom Intro Song', type: 'song', required: false, placeholder: 'Song for the couple entrance', mapTo: { target: 'music', field: 'entranceSong' } },
          { id: 'brideAndGroom', label: 'Bride & Groom', type: 'text', required: false, placeholder: 'How should they be announced?' },
          { id: 'howAnnounced', label: 'How will you be announced', type: 'text', required: false, placeholder: 'e.g. Mr. & Mrs. Smith, The Smiths' },
        ],
      },
      {
        id: 'parentsIntros',
        title: 'Parents Introductions',
        description: 'Parent walk-in details',
        layout: 'paired',
        fields: [
          { id: 'parentsWalkingIn', label: 'Will Parents be walking in?', type: 'yes_no', required: false, fullWidth: true },
          { id: 'brideParents', label: "Bride's Parents", type: 'text', required: false, placeholder: 'Names', conditional: { field: 'parentsWalkingIn', value: 'yes' } },
          { id: 'brideWalkInSong', label: 'Walk in Song', type: 'song', required: false, placeholder: 'Song title — Artist', conditional: { field: 'parentsWalkingIn', value: 'yes' } },
          { id: 'brideMom', label: "Bride's Mom", type: 'text', required: false, placeholder: 'Name', conditional: { field: 'parentsWalkingIn', value: 'yes' } },
          { id: 'brideDad', label: "Bride's Dad", type: 'text', required: false, placeholder: 'Name', conditional: { field: 'parentsWalkingIn', value: 'yes' } },
          { id: 'groomParents', label: "Groom's Parents", type: 'text', required: false, placeholder: 'Names', conditional: { field: 'parentsWalkingIn', value: 'yes' } },
          { id: 'groomWalkInSong', label: 'Walk in Song 2', type: 'song', required: false, placeholder: 'Song title — Artist', conditional: { field: 'parentsWalkingIn', value: 'yes' } },
          { id: 'groomMom', label: "Groom's Mom", type: 'text', required: false, placeholder: 'Name', conditional: { field: 'parentsWalkingIn', value: 'yes' } },
          { id: 'groomDad', label: "Groom's Dad", type: 'text', required: false, placeholder: 'Name', conditional: { field: 'parentsWalkingIn', value: 'yes' } },
        ],
      },
      {
        id: 'brideGroomDance',
        title: 'Bride & Groom Dance',
        description: 'First dance details',
        fields: [
          { id: 'brideGroomDance', label: 'Bride & Groom Dance', type: 'yes_no', required: false },
          { id: 'firstDanceSong', label: 'First Dance Song', type: 'song', required: false, placeholder: 'Song title — Artist', conditional: { field: 'brideGroomDance', value: 'yes' }, mapTo: { target: 'music', field: 'firstDanceSong' } },
          { id: 'firstDanceDuration', label: 'How long will you be dancing', type: 'text', required: false, placeholder: 'e.g. Full song, 2 minutes, fade out after chorus', conditional: { field: 'brideGroomDance', value: 'yes' } },
          { id: 'inviteBridalParty', label: 'Invite bridal party?', type: 'yes_no', required: false },
        ],
      },
      {
        id: 'parentsDances',
        title: 'Parents Dances',
        description: 'Parent dance details',
        fields: [
          { id: 'brideFatherDance', label: 'Bride & Father Dance', type: 'yes_no', required: false },
          { id: 'brideFatherSong', label: 'B&F Song', type: 'song', required: false, placeholder: 'Song title — Artist', conditional: { field: 'brideFatherDance', value: 'yes' }, mapTo: { target: 'music', field: 'fatherDaughterSong' } },
          { id: 'brideFatherDuration', label: 'How long will you dance for', type: 'text', required: false, placeholder: 'e.g. Full song, 2 minutes', conditional: { field: 'brideFatherDance', value: 'yes' } },
          { id: 'groomMotherDance', label: 'Groom & Mother Dance', type: 'yes_no', required: false },
          { id: 'groomMotherSong', label: 'G&M Song', type: 'song', required: false, placeholder: 'Song title — Artist', conditional: { field: 'groomMotherDance', value: 'yes' }, mapTo: { target: 'music', field: 'motherSonSong' } },
          { id: 'groomMotherDuration', label: 'How long will you dance', type: 'text', required: false, placeholder: 'e.g. Full song, 2 minutes', conditional: { field: 'groomMotherDance', value: 'yes' } },
        ],
      },
      {
        id: 'toastsBlessings',
        title: 'Toasts & Blessings',
        description: 'Speeches and blessings',
        fields: [
          { id: 'toastsBy', label: 'Toasts by', type: 'text', required: false, placeholder: 'Best man, maid of honor, parents, etc.', mapTo: { target: 'timeline', field: 'toastBy' } },
          { id: 'blessingsBy', label: 'Blessings by', type: 'text', required: false, placeholder: 'Who gives the blessing?' },
        ],
      },
      {
        id: 'other',
        title: 'Other Information',
        description: 'Music preferences, lighting, and special requests',
        fields: otherInfoFields(),
      },
    ],
  },

  // ── SWEET 16 ─────────────────────────────────────────────────────────
  sweet16: {
    eventType: 'sweet16',
    title: 'Sweet 16 Planning Form',
    description: 'Let\'s plan an amazing Sweet 16! Share the details below so we can make it perfect.',
    steps: [
      {
        id: 'general',
        title: 'General Info',
        description: 'Basic event and contact details',
        fields: generalInfoFields('Sweet 16'),
      },
      {
        id: 'highlights',
        title: 'Event Highlights',
        description: 'Key moments for the celebration',
        fields: [
          { id: 'cocktailMusic', label: 'Cocktail Music', type: 'yes_no', required: false, mapTo: { target: 'music', field: 'cocktailMusic' } },
          { id: 'cocktailMusicType', label: 'Cocktail Music Type', type: 'text', required: false, placeholder: 'e.g. Jazz, lounge, acoustic', conditional: { field: 'cocktailMusic', value: 'yes' }, mapTo: { target: 'music', field: 'cocktailMusicStyle' } },
          { id: 'dinnerMusic', label: 'Dinner Music', type: 'yes_no', required: false, mapTo: { target: 'music', field: 'dinnerMusic' } },
          { id: 'dinnerMusicType', label: 'Dinner Music Type', type: 'text', required: false, placeholder: 'e.g. Soft background, classic hits', conditional: { field: 'dinnerMusic', value: 'yes' }, mapTo: { target: 'music', field: 'dinnerMusicStyle' } },
          { id: 'speech', label: 'Speech', type: 'yes_no', required: false },
          { id: 'speechBy', label: 'Speech By', type: 'text', required: false, placeholder: 'Who is giving the speech?', conditional: { field: 'speech', value: 'yes' }, mapTo: { target: 'timeline', field: 'speechBy' } },
          { id: 'toast', label: 'Toast', type: 'yes_no', required: false },
          { id: 'toastBy', label: 'Toast By', type: 'text', required: false, placeholder: 'Who is giving the toast?', conditional: { field: 'toast', value: 'yes' }, mapTo: { target: 'timeline', field: 'toastBy' } },
          { id: 'blessing', label: 'Blessing', type: 'yes_no', required: false },
          { id: 'blessingBy', label: 'Blessing By', type: 'text', required: false, placeholder: 'Who gives the blessing?', conditional: { field: 'blessing', value: 'yes' } },
          { id: 'dinnerServed', label: 'Dinner Served', type: 'yes_no', required: false },
          { id: 'dinnerTime', label: 'Dinner Time', type: 'time', required: false, conditional: { field: 'dinnerServed', value: 'yes' }, mapTo: { target: 'timeline', field: 'dinnerTime' } },
          { id: 'dinnerStyle', label: 'Style', type: 'dropdown', required: false, options: ['Buffet', 'Family Style', 'Individually Served'], conditional: { field: 'dinnerServed', value: 'yes' }, mapTo: { target: 'logistics', field: 'dinnerStyle' } },
          { id: 'honorDance', label: 'Honor Dance / Dad & Daughter Dance', type: 'yes_no', required: false },
          { id: 'honorDanceSong', label: 'Honor Dance Song', type: 'song', required: false, placeholder: 'Song title — Artist', conditional: { field: 'honorDance', value: 'yes' }, mapTo: { target: 'music', field: 'honorDanceSong' } },
          { id: 'firstDance', label: 'First Dance of Evening', type: 'yes_no', required: false },
          { id: 'firstDanceSong', label: 'First Dance Song', type: 'song', required: false, placeholder: 'Song title — Artist', conditional: { field: 'firstDance', value: 'yes' }, mapTo: { target: 'music', field: 'firstDanceSong' } },
          { id: 'lastDance', label: 'Last Dance of Evening', type: 'yes_no', required: false },
          { id: 'lastDanceSong', label: 'Last Dance Song', type: 'song', required: false, placeholder: 'The final song of the night', conditional: { field: 'lastDance', value: 'yes' }, mapTo: { target: 'music', field: 'lastDanceSong' } },
          { id: 'specialDances', label: 'Other Special Dances', type: 'yes_no', required: false },
          { id: 'specialDancesList', label: 'List Dances', type: 'textarea', required: false, placeholder: 'Mother-daughter dance, friend dance, group dance, etc.', conditional: { field: 'specialDances', value: 'yes' } },
          { id: 'montage', label: 'Video Presentation', type: 'yes_no', required: false },
          { id: 'montageTiming', label: 'Video When', type: 'dropdown', required: false, options: ['Before Dinner', 'During Dinner', 'After Dinner'], conditional: { field: 'montage', value: 'yes' } },
          { id: 'crownPlacement', label: 'Crown Placement', type: 'yes_no', required: false },
          { id: 'crownBy', label: 'By Who?', type: 'text', required: false, placeholder: 'e.g. Father, Mother', conditional: { field: 'crownPlacement', value: 'yes' } },
          { id: 'shoeChange', label: 'Changing of Shoes', type: 'yes_no', required: false },
          { id: 'shoeChangeBy', label: 'By?', type: 'text', required: false, placeholder: 'e.g. Father, Mother', conditional: { field: 'shoeChange', value: 'yes' } },
          { id: 'games', label: 'Game During Party?', type: 'yes_no', required: false },
          { id: 'gamesDetails', label: 'Game Details', type: 'textarea', required: false, placeholder: 'Describe any games, contests, or interactive activities...', conditional: { field: 'games', value: 'yes' } },
          { id: 'dancersTshirts', label: 'Dancers T-Shirts?', type: 'yes_no', required: false },
          { id: 'dancersAttireColor', label: 'Dancers Attire Color', type: 'text', required: false, placeholder: 'e.g. Black, neon, match event colors', conditional: { field: 'dancersTshirts', value: 'yes' }, mapTo: { target: 'staffing', field: 'dancersAttireColor' } },
        ],
      },
      {
        id: 'introductions',
        title: 'VIP Introductions',
        description: 'Court and family introductions',
        layout: 'paired',
        fields: [
          { id: 'court1Names', label: 'Court 1 Names', type: 'text', required: false, placeholder: 'Names for court 1' },
          { id: 'court1Song', label: 'Song 1', type: 'song', required: false, placeholder: 'Song title — Artist' },
          { id: 'court2Names', label: 'Court 2 Names', type: 'text', required: false, placeholder: 'Names for court 2' },
          { id: 'court2Song', label: 'Song 2', type: 'song', required: false, placeholder: 'Song title — Artist' },
          { id: 'court3Names', label: 'Court 3 Names', type: 'text', required: false, placeholder: 'Names for court 3' },
          { id: 'court3Song', label: 'Song 3', type: 'song', required: false, placeholder: 'Song title — Artist' },
          { id: 'court4Names', label: 'Court 4 Names', type: 'text', required: false, placeholder: 'Names for court 4' },
          { id: 'court4Song', label: 'Song 4', type: 'song', required: false, placeholder: 'Song title — Artist' },
          { id: 'court5Names', label: 'Court 5 Names', type: 'text', required: false, placeholder: 'Names for court 5' },
          { id: 'court5Song', label: 'Song 5', type: 'song', required: false, placeholder: 'Song title — Artist' },
          { id: 'court6Names', label: 'Court 6 Names', type: 'text', required: false, placeholder: 'Names for court 6' },
          { id: 'court6Song', label: 'Song 6', type: 'song', required: false, placeholder: 'Song title — Artist' },
          { id: 'court7Names', label: 'Court 7 Names', type: 'text', required: false, placeholder: 'Names for court 7' },
          { id: 'court7Song', label: 'Song 7', type: 'song', required: false, placeholder: 'Song title — Artist' },
          { id: 'court8Names', label: 'Court 8 Names', type: 'text', required: false, placeholder: 'Names for court 8' },
          { id: 'court8Song', label: 'Song 8', type: 'song', required: false, placeholder: 'Song title — Artist' },
          { id: 'familyNames', label: 'Family Names', type: 'text', required: false, placeholder: 'e.g. Mom Lisa & Dad Chris', mapTo: { target: 'timeline', field: 'familyIntros' } },
          { id: 'familyIntroSong', label: 'Intro Song', type: 'song', required: false, placeholder: 'Song for family intro' },
          { id: 'gohEscortedBy', label: 'Guest of Honor / Escorted By', type: 'text', required: false, placeholder: 'e.g. Escorted by Liv & Marina - GOH Name', mapTo: { target: 'timeline', field: 'guestOfHonorIntro' } },
          { id: 'gohSong', label: 'Song', type: 'song', required: false, placeholder: 'Song for guest of honor entrance', mapTo: { target: 'music', field: 'entranceSong' } },
        ],
      },
      {
        id: 'candles',
        title: 'Candle Lighting',
        description: 'The 16 + 1 candle lighting ceremony',
        layout: 'paired',
        fields: [
          { id: 'candleLighting', label: 'Will there be a Candle Lighting Ceremony?', type: 'yes_no', required: true, fullWidth: true },
          { id: 'candleLightingSong', label: 'Background song for candle lighting', type: 'song', required: false, placeholder: 'Instrumental or soft background', conditional: { field: 'candleLighting', value: 'yes' }, fullWidth: true },
          ...buildCandleFields(17, 'Sweet 16').map(f => ({ ...f, conditional: { field: 'candleLighting', value: 'yes' } })),
        ],
      },
      {
        id: 'other',
        title: 'Other Information',
        description: 'Music preferences and special requests',
        fields: otherInfoFields(),
      },
    ],
  },

  // ── CORPORATE ────────────────────────────────────────────────────────
  corporate: {
    eventType: 'corporate',
    title: 'Corporate Event Planning Form',
    description: 'Help us understand your event needs so we can deliver a seamless experience.',
    steps: [
      {
        id: 'general',
        title: 'General Info',
        description: 'Event details and logistics',
        fields: [
          { id: 'eventName', label: 'Event Name / Title', type: 'text', required: true, critical: true, placeholder: 'e.g. Annual Company Gala', mapTo: { target: 'planning', field: 'guestOfHonor' } },
          { id: 'companyName', label: 'Company / Organization', type: 'text', required: true, critical: true, placeholder: 'Company name' },
          { id: 'eventDate', label: 'Event Date', type: 'date', required: true, critical: true, mapTo: { target: 'planning', field: 'eventDate' } },
          { id: 'startTime', label: 'Start Time', type: 'time', required: true, mapTo: { target: 'logistics', field: 'eventStartTime' } },
          { id: 'endTime', label: 'End Time', type: 'time', required: true, mapTo: { target: 'logistics', field: 'eventEndTime' } },
          { id: 'venueName', label: 'Venue Name', type: 'text', required: true, placeholder: 'Venue name', mapTo: { target: 'logistics', field: 'venueName' } },
          { id: 'guestCount', label: 'Expected Attendees', type: 'number', required: true, placeholder: '0', mapTo: { target: 'planning', field: 'adultCount' } },
          { id: 'eventFormat', label: 'Event Format', type: 'dropdown', required: false, options: ['Seated Dinner', 'Cocktail Reception', 'Conference/Presentation', 'Awards Ceremony', 'Team Building', 'Holiday Party', 'Other'] },
          { id: 'contactName', label: 'Event Coordinator', type: 'text', required: true, placeholder: 'Point of contact' },
          { id: 'contactPhone', label: 'Coordinator Phone', type: 'phone', required: true, placeholder: '(555) 123-4567' },
        ],
      },
      {
        id: 'highlights',
        title: 'Event Highlights',
        description: 'Speeches, entertainment, and key moments',
        fields: [
          { id: 'cocktailMusic', label: 'Cocktail Music', type: 'yes_no', required: false, mapTo: { target: 'music', field: 'cocktailMusic' } },
          { id: 'cocktailMusicType', label: 'Cocktail Music Type', type: 'text', required: false, placeholder: 'e.g. Jazz, lounge, background', conditional: { field: 'cocktailMusic', value: 'yes' }, mapTo: { target: 'music', field: 'cocktailMusicStyle' } },
          { id: 'dinnerMusic', label: 'Dinner Music', type: 'yes_no', required: false, mapTo: { target: 'music', field: 'dinnerMusic' } },
          { id: 'dinnerMusicType', label: 'Dinner Music Type', type: 'text', required: false, placeholder: 'e.g. Soft instrumental, classic', conditional: { field: 'dinnerMusic', value: 'yes' }, mapTo: { target: 'music', field: 'dinnerMusicStyle' } },
          { id: 'speeches', label: 'Speeches / Presentations', type: 'yes_no', required: false },
          { id: 'speechDetails', label: 'Speech Details', type: 'textarea', required: false, placeholder: 'Who is speaking? How long? Any AV needs?', showIf: { field: 'speeches', value: 'yes' }, mapTo: { target: 'timeline', field: 'toasts' } },
          { id: 'awards', label: 'Awards or recognition ceremony?', type: 'yes_no', required: false },
          { id: 'awardsDetails', label: 'Awards Details', type: 'textarea', required: false, placeholder: 'How many awards? Any specific songs or walk-up music?', showIf: { field: 'awards', value: 'yes' } },
          { id: 'entertainment', label: 'Live entertainment needed?', type: 'yes_no', required: false },
          { id: 'entertainmentDetails', label: 'Entertainment Details', type: 'textarea', required: false, placeholder: 'Band, DJ, performers, etc.', showIf: { field: 'entertainment', value: 'yes' } },
          { id: 'avRequirements', label: 'AV / Technical Requirements', type: 'textarea', required: false, placeholder: 'Microphones, projectors, screens, monitors, special lighting...', showIfAny: [{ field: 'speeches', value: 'yes' }, { field: 'awards', value: 'yes' }, { field: 'entertainment', value: 'yes' }] },
          { id: 'dinnerServed', label: 'Dinner Served', type: 'yes_no', required: false },
          { id: 'dinnerServedWhen', label: 'When is Dinner Served', type: 'text', required: false, placeholder: 'e.g. After cocktail hour, 7:00 PM', conditional: { field: 'dinnerServed', value: 'yes' }, mapTo: { target: 'timeline', field: 'dinnerTiming' } },
          { id: 'montage', label: 'Montage / Video Presentation', type: 'yes_no', required: false },
          { id: 'montageTiming', label: 'Video Presentation When', type: 'dropdown', required: false, options: ['Before Dinner', 'During Dinner', 'After Dinner', 'During Presentations'], conditional: { field: 'montage', value: 'yes' } },
          { id: 'dancing', label: 'Will there be a dance portion?', type: 'yes_no', required: false },
          { id: 'danceStyle', label: 'Dance Music Style', type: 'text', required: false, placeholder: 'e.g. Top 40, classic, mix of everything', showIf: { field: 'dancing', value: 'yes' } },
          { id: 'lastDance', label: 'Last Dance', type: 'yes_no', required: false },
          { id: 'lastDanceSong', label: 'Last Song', type: 'song', required: false, placeholder: 'Final song of the night', conditional: { field: 'lastDance', value: 'yes' }, mapTo: { target: 'music', field: 'lastDanceSong' } },
          { id: 'brandingNeeds', label: 'Any branding/logo display needs?', type: 'textarea', required: false, placeholder: 'Gobo projection, screens, signage, etc.' },
        ],
      },
      {
        id: 'other',
        title: 'Other Information',
        description: 'Music, atmosphere, and special notes',
        fields: otherInfoFields(),
      },
    ],
  },

  // ── SCHOOL ───────────────────────────────────────────────────────────
  school: {
    eventType: 'school',
    title: 'School Event Planning Form',
    description: 'Help us plan your school event! Fill out the details so we can make it amazing.',
    steps: [
      {
        id: 'general',
        title: 'General Info',
        description: 'Event details',
        fields: [
          { id: 'schoolName', label: 'School Name', type: 'text', required: true, critical: true, placeholder: 'School name', mapTo: { target: 'planning', field: 'guestOfHonor' } },
          { id: 'eventName', label: 'Event Name', type: 'text', required: true, critical: true, placeholder: 'e.g. Prom, Homecoming, Spring Dance' },
          { id: 'eventDate', label: 'Event Date', type: 'date', required: true, critical: true, mapTo: { target: 'planning', field: 'eventDate' } },
          { id: 'setupTime', label: 'Setup / Load-In Time', type: 'time', required: false, mapTo: { target: 'logistics', field: 'loadInTime' } },
          { id: 'studentArrival', label: 'Student Arrival Time', type: 'time', required: true, mapTo: { target: 'logistics', field: 'eventStartTime' } },
          { id: 'endTime', label: 'Event End Time', type: 'time', required: true, mapTo: { target: 'logistics', field: 'eventEndTime' } },
          { id: 'venueName', label: 'Venue / Location', type: 'text', required: true, placeholder: 'Gym, ballroom, outdoor, etc.', mapTo: { target: 'logistics', field: 'venueName' } },
          { id: 'studentCount', label: 'Expected Students', type: 'number', required: true, placeholder: '0', mapTo: { target: 'planning', field: 'adultCount' } },
          { id: 'contactName', label: 'Staff Contact', type: 'text', required: true, placeholder: 'Teacher or administrator name' },
          { id: 'contactPhone', label: 'Contact Phone', type: 'phone', required: true, placeholder: '(555) 123-4567' },
        ],
      },
      {
        id: 'highlights',
        title: 'Dance Highlights',
        description: 'Key moments and traditions',
        fields: [
          { id: 'danceType', label: 'Type of Dance', type: 'dropdown', required: false, options: ['Prom', 'Homecoming', 'Semi-Formal', 'Spring Dance', 'Winter Formal', 'Graduation Dance', 'Other'] },
          { id: 'courtCeremony', label: 'Court Ceremony (King/Queen)?', type: 'yes_no', required: false },
          { id: 'courtDetails', label: 'Court Details', type: 'textarea', required: false, placeholder: 'Names, walk-out order, crowning details...', conditional: { field: 'courtCeremony', value: 'yes' }, mapTo: { target: 'timeline', field: 'courtIntros' } },
          { id: 'courtSong', label: 'Court Walk-Out Song', type: 'song', required: false, placeholder: 'Song title — Artist', conditional: { field: 'courtCeremony', value: 'yes' } },
          { id: 'speech', label: 'Speech / Announcements', type: 'yes_no', required: false },
          { id: 'speechBy', label: 'Speech By', type: 'text', required: false, placeholder: 'Principal, student council, etc.', conditional: { field: 'speech', value: 'yes' }, mapTo: { target: 'timeline', field: 'speechBy' } },
          { id: 'announcements', label: 'Other Announcements', type: 'textarea', required: false, placeholder: 'Thank yous, sponsors, raffle winners, etc.', mapTo: { target: 'planning', field: 'mcNotes' } },
          { id: 'montage', label: 'Montage / Video', type: 'yes_no', required: false },
          { id: 'montageTiming', label: 'Video When', type: 'dropdown', required: false, options: ['Beginning of Night', 'Middle of Night', 'Before Last Dance'], conditional: { field: 'montage', value: 'yes' } },
          { id: 'specialDances', label: 'Any special dances or moments?', type: 'textarea', required: false, placeholder: 'Slow dance, line dance, etc.' },
          { id: 'lastDance', label: 'Last Dance', type: 'yes_no', required: false },
          { id: 'lastDanceSong', label: 'Last Song', type: 'song', required: false, placeholder: 'Final song of the night', conditional: { field: 'lastDance', value: 'yes' }, mapTo: { target: 'music', field: 'lastDanceSong' } },
          { id: 'games', label: 'Games / Activities?', type: 'yes_no', required: false },
          { id: 'gamesDetails', label: 'Game Details', type: 'textarea', required: false, placeholder: 'Photo booth, dance contests, limbo...', conditional: { field: 'games', value: 'yes' } },
          { id: 'dancersTshirts', label: 'Dancers T-Shirts?', type: 'yes_no', required: false },
          { id: 'dancersAttireColor', label: 'Dancers Attire Color', type: 'text', required: false, placeholder: 'e.g. School colors, neon', conditional: { field: 'dancersTshirts', value: 'yes' }, mapTo: { target: 'staffing', field: 'dancersAttireColor' } },
        ],
      },
      {
        id: 'other',
        title: 'Other Information',
        description: 'Music preferences and notes',
        fields: otherInfoFields(),
      },
    ],
  },

  // ── QUINCEANERA ──────────────────────────────────────────────────────
  quinceanera: {
    eventType: 'quinceanera',
    title: 'Quinceañera Planning Form',
    description: 'Let\'s plan a beautiful Quinceañera! Share the details so our team can make every moment special.',
    steps: [
      {
        id: 'general',
        title: 'General Info',
        description: 'Basic event and contact details',
        fields: generalInfoFields('Quinceañera'),
      },
      {
        id: 'highlights',
        title: 'Quinceañera Highlights',
        description: 'Traditions and key moments',
        fields: [
          { id: 'cocktailMusic', label: 'Cocktail Music', type: 'yes_no', required: false, mapTo: { target: 'music', field: 'cocktailMusic' } },
          { id: 'cocktailMusicType', label: 'Cocktail Music Type', type: 'text', required: false, placeholder: 'e.g. Latin jazz, acoustic, lounge', conditional: { field: 'cocktailMusic', value: 'yes' }, mapTo: { target: 'music', field: 'cocktailMusicStyle' } },
          { id: 'dinnerMusic', label: 'Dinner Music', type: 'yes_no', required: false, mapTo: { target: 'music', field: 'dinnerMusic' } },
          { id: 'dinnerMusicType', label: 'Dinner Music Type', type: 'text', required: false, placeholder: 'e.g. Soft background, romantic ballads', conditional: { field: 'dinnerMusic', value: 'yes' }, mapTo: { target: 'music', field: 'dinnerMusicStyle' } },
          { id: 'introductions', label: 'Introductions', type: 'yes_no', required: false },
          { id: 'introductionMusic', label: 'Introduction Music', type: 'song', required: false, placeholder: 'Song for introductions', conditional: { field: 'introductions', value: 'yes' }, mapTo: { target: 'music', field: 'introSong' } },
          { id: 'speech', label: 'Speech', type: 'yes_no', required: false },
          { id: 'speechBy', label: 'Speech By', type: 'text', required: false, placeholder: 'Who is giving the speech?', conditional: { field: 'speech', value: 'yes' }, mapTo: { target: 'timeline', field: 'speechBy' } },
          { id: 'blessing', label: 'Blessing', type: 'yes_no', required: false },
          { id: 'blessingBy', label: 'Blessing By', type: 'text', required: false, placeholder: 'Who gives the blessing?', conditional: { field: 'blessing', value: 'yes' } },
          { id: 'dinnerServed', label: 'Dinner Served', type: 'yes_no', required: false },
          { id: 'dinnerStyle', label: 'Style', type: 'dropdown', required: false, options: ['Buffet', 'Buffet Line', 'Plated', 'Family Style', 'Stations', 'Other'], conditional: { field: 'dinnerServed', value: 'yes' }, mapTo: { target: 'logistics', field: 'dinnerStyle' } },
          { id: 'cakeCutting', label: 'Cake Cutting', type: 'yes_no', required: false },
          { id: 'cakeCuttingWhen', label: 'When', type: 'text', required: false, placeholder: 'e.g. After Dinner, Before Dancing', conditional: { field: 'cakeCutting', value: 'yes' }, mapTo: { target: 'timeline', field: 'cakeCuttingTiming' } },
          { id: 'lastDoll', label: 'Last Doll Ceremony', type: 'yes_no', required: false },
          { id: 'dollGiver', label: 'Doll Giver', type: 'text', required: false, placeholder: 'Who gives the last doll?', conditional: { field: 'lastDoll', value: 'yes' } },
          { id: 'fatherDance', label: 'Father & Daughter Dance', type: 'yes_no', required: false },
          { id: 'fatherDanceSong', label: 'Father & Daughter Song', type: 'song', required: false, placeholder: 'Song title — Artist', conditional: { field: 'fatherDance', value: 'yes' }, mapTo: { target: 'music', field: 'fatherDaughterSong' } },
          { id: 'gohGrandparent', label: 'GOH & Grand Parent', type: 'yes_no', required: false },
          { id: 'gohGrandparentSong', label: 'GOH & GP Song', type: 'song', required: false, placeholder: 'Song title — Artist', conditional: { field: 'gohGrandparent', value: 'yes' }, mapTo: { target: 'music', field: 'gohGrandparentSong' } },
          { id: 'courtDance', label: 'Court Dance', type: 'yes_no', required: false },
          { id: 'courtDanceSong', label: 'Court Performance Song', type: 'song', required: false, placeholder: 'Song for court performance', conditional: { field: 'courtDance', value: 'yes' }, mapTo: { target: 'music', field: 'courtDanceSong' } },
          { id: 'lastDance', label: 'Last Dance of Evening', type: 'yes_no', required: false },
          { id: 'lastDanceSong', label: 'Last Dance Song', type: 'song', required: false, placeholder: 'Final song of the night', conditional: { field: 'lastDance', value: 'yes' }, mapTo: { target: 'music', field: 'lastDanceSong' } },
          { id: 'specialDances', label: 'Other Special Dances', type: 'yes_no', required: false },
          { id: 'specialDancesList', label: 'List Special Dances', type: 'textarea', required: false, placeholder: 'Waltz, group dance, surprise dance, etc.', conditional: { field: 'specialDances', value: 'yes' } },
        ],
      },
      {
        id: 'introductions',
        title: 'Party Introductions',
        description: 'Court and family introductions',
        fields: [
          { id: 'parents', label: 'Parents', type: 'text', required: false, placeholder: 'e.g. Jeffrey and Jessica (mom & dad)' },
          { id: 'siblings', label: 'Siblings', type: 'text', required: false, placeholder: 'Names and any notes' },
          { id: 'crownCarrier', label: 'Crown Carrier', type: 'text', required: false, placeholder: 'Who carries/places the crown?' },
          { id: 'shoeCarrier', label: 'Shoe Carrier', type: 'text', required: false, placeholder: 'Who carries/changes the shoes?' },
          { id: 'courtDuo1', label: 'Court Duo 1', type: 'text', required: false, placeholder: 'Name(s)' },
          { id: 'courtDuo2', label: 'Court Duo 2', type: 'text', required: false, placeholder: 'Name(s)' },
          { id: 'courtDuo3', label: 'Court Duo 3', type: 'text', required: false, placeholder: 'Name(s)' },
          { id: 'courtDuo4', label: 'Court Duo 4', type: 'text', required: false, placeholder: 'Name(s)' },
          { id: 'courtDuo5', label: 'Court Duo 5', type: 'text', required: false, placeholder: 'Name(s)' },
          { id: 'courtDuo6', label: 'Court Duo 6', type: 'text', required: false, placeholder: 'Name(s)' },
          { id: 'courtDuo7', label: 'Court Duo 7', type: 'text', required: false, placeholder: 'Name(s)' },
          { id: 'guestOfHonorIntro', label: 'Guest Of Honor', type: 'text', required: false, placeholder: 'Name of the Quinceañera', mapTo: { target: 'timeline', field: 'guestOfHonorIntro' } },
        ],
      },
      {
        id: 'candles',
        title: 'Candle Lighting',
        description: 'The 15 candle lighting ceremony',
        layout: 'paired',
        fields: [
          { id: 'candleLighting', label: 'Will there be a Candle Lighting Ceremony?', type: 'yes_no', required: true, fullWidth: true },
          { id: 'candleLightingSong', label: 'Background song for candle lighting', type: 'song', required: false, placeholder: 'Instrumental or soft background', conditional: { field: 'candleLighting', value: 'yes' }, fullWidth: true },
          ...buildCandleFields(15, 'Quinceañera').map(f => ({ ...f, conditional: { field: 'candleLighting', value: 'yes' } })),
        ],
      },
      {
        id: 'other',
        title: 'Other Information',
        description: 'Music preferences and special requests',
        fields: otherInfoFields(),
      },
    ],
  },

  // ── PARTY ────────────────────────────────────────────────────────────
  songRequest: {
    eventType: 'songRequest',
    title: 'Music & Song Requests',
    description: 'Share the music that will make your event feel like you. Drop a Spotify playlist link, or search and add specific songs below.',
    steps: [
      {
        id: 'music',
        title: 'Music & Song Requests',
        description: 'Songs, vibe, and your Spotify playlist',
        fields: [
          { id: 'spotifyPlaylist', label: 'Submit Your Spotify Playlist Link Here', type: 'url', required: false, placeholder: 'https://open.spotify.com/playlist/...', mapTo: { target: 'music', field: 'spotifyPlaylist' } },
          { id: 'songRequests', label: 'Song Requests', type: 'repeating_group', required: false, itemLabel: 'song', addLabel: '+ Add another song', defaultItems: 3, minItems: 1, maxItems: 25, itemFields: [ { id: 'song', label: 'Song', type: 'song', placeholder: 'Search, or type: Song title — Artist' } ], mapTo: { target: 'music', field: 'songRequests' } },
          { id: 'songRequestPage', label: 'Song Requests', type: 'song_picker', required: false, fullWidth: true },
          { id: 'musicStyle', label: 'Overall Music Style', type: 'textarea', required: false, placeholder: 'e.g. Top 40, R&B, classic hits, lounge during dinner...', mapTo: { target: 'music', field: 'musicStyle' } },
          { id: 'specialRequests', label: 'Other Info', type: 'textarea', rows: 6, required: false, placeholder: 'Anything else — extra details, requests, questions, or anything not covered above.', mapTo: { target: 'planning', field: 'specialRequests' } },
        ],
      },
    ],
  },
  // ── EVENT ESSENTIALS ─────────────────────────────────────────────────
  // For event types that do NOT need a full production flow -- private
  // parties, birthdays, graduations, anniversaries, "Other".
  //
  // Decision (Mo, 19 Aug 2026): those clients keep a LEAN form -- no
  // introductions, no candle lighting, no parent dances, no montage. But
  // the crew still has to find the room and get gear into it, and the
  // song-only form asked for none of that. Measured across the 8 live lean
  // events on that date: start/end time 8/8 and address 7/8 (both arrive
  // from the lead form), but venue NAME 5/8, and room / floor / elevator /
  // setting / guest arrival / load-in 0/8 -- nothing ever asked.
  //
  // Step 1 is the crew-critical subset of generalInfoFields plus two fields
  // the full `party` schema does not have (setupTime, parkingNotes).
  // Step 2 is the EXACT music step these clients already see today, so
  // nothing they were used to is taken away.
  essentials: {
    eventType: 'essentials',
    title: 'Event Details',
    description: 'A few details so our team can arrive ready — then the music.',
    steps: [
      {
        id: 'general',
        title: 'Event Essentials',
        description: 'Where, when, and who we coordinate with',
        fields: [
          { id: 'guestOfHonor', label: 'Guest of Honor', type: 'text', required: true, critical: true, placeholder: 'Full name', mapTo: { target: 'planning', field: 'guestOfHonor' } },
          { id: 'eventDate', label: 'Event Date', type: 'date', required: true, critical: true, prefillFrom: 'eventDate', mapTo: { target: 'planning', field: 'eventDate' } },
          { id: 'startTime', label: 'Event Start Time', type: 'time', required: true, critical: true, prefillFrom: 'startTime', mapTo: { target: 'logistics', field: 'eventStartTime' } },
          { id: 'endTime', label: 'Event End Time', type: 'time', required: true, prefillFrom: 'endTime', mapTo: { target: 'logistics', field: 'eventEndTime' } },
          { id: 'setupTime', label: 'Setup / Load-In Time', type: 'time', required: false, mapTo: { target: 'logistics', field: 'loadInTime' } },
          { id: 'venueName', label: 'Venue Name', type: 'text', required: true, critical: true, prefillFrom: 'venue', placeholder: 'e.g. The Grand Ballroom', mapTo: { target: 'logistics', field: 'venueName' } },
          { id: 'venueAddress', label: 'Venue Address', type: 'text', required: false, placeholder: 'Full address', mapTo: { target: 'logistics', field: 'venueAddress' } },
          { id: 'venuePhone', label: 'Venue Phone', type: 'phone', required: false, placeholder: '(555) 000-0000', mapTo: { target: 'logistics', field: 'venuePhone' } },
          { id: 'roomName', label: 'Room Name', type: 'text', required: false, placeholder: 'e.g. Grand Ballroom A, Terrace Room', mapTo: { target: 'logistics', field: 'roomName' } },
          { id: 'roomFloor', label: 'What floor is the room on', type: 'text', required: false, placeholder: 'e.g. 2nd floor, Ground level', mapTo: { target: 'logistics', field: 'roomFloor' } },
          { id: 'elevator', label: 'Elevator Access', type: 'yes_no', required: false, mapTo: { target: 'logistics', field: 'elevatorAccess' } },
          { id: 'setting', label: 'Setting', type: 'dropdown', required: false, options: ['Indoors', 'Outdoors', 'Both'], mapTo: { target: 'logistics', field: 'setting' } },
          { id: 'parkingNotes', label: 'Parking / Unloading Notes', type: 'textarea', required: false, placeholder: 'Where should our team park and unload gear?', mapTo: { target: 'logistics', field: 'parkingNotes' } },
          { id: 'totalGuests', label: 'Number of Guests', type: 'number', required: false, placeholder: 'Total expected', mapTo: { target: 'planning', field: 'totalGuests' } },
          { id: 'adultCount', label: 'How Many Adults', type: 'number', required: true, critical: true, placeholder: '0', mapTo: { target: 'planning', field: 'adultCount' } },
          { id: 'childCount', label: 'How Many Kids', type: 'number', required: false, placeholder: '0', mapTo: { target: 'planning', field: 'childCount' } },
          { id: 'guestArrival', label: 'Guest Arrival', type: 'time', required: false, mapTo: { target: 'logistics', field: 'guestArrivalTime' } },
          { id: 'contactName', label: 'Primary Contact Name', type: 'text', required: true, critical: true, placeholder: 'Who should we coordinate with?' },
          { id: 'contactPhone', label: 'Contact Phone', type: 'phone', required: true, critical: true, placeholder: '(555) 123-4567' },
          { id: 'contactEmail', label: 'Contact Email', type: 'email', required: false, placeholder: 'email@example.com' },
        ],
      },
      {
        id: 'music',
        title: 'Music & Notes',
        description: 'Songs, vibe, and your Spotify playlist',
        fields: [
          { id: 'spotifyPlaylist', label: 'Submit Your Spotify Playlist Link Here', type: 'url', required: false, placeholder: 'https://open.spotify.com/playlist/...', mapTo: { target: 'music', field: 'spotifyPlaylist' } },
          { id: 'songRequests', label: 'Song Requests', type: 'repeating_group', required: false, itemLabel: 'song', addLabel: '+ Add another song', defaultItems: 3, minItems: 1, maxItems: 25, itemFields: [ { id: 'song', label: 'Song', type: 'song', placeholder: 'Search, or type: Song title — Artist' } ], mapTo: { target: 'music', field: 'songRequests' } },
          { id: 'songRequestPage', label: 'Song Requests', type: 'song_picker', required: false, fullWidth: true },
          { id: 'musicStyle', label: 'Overall Music Style', type: 'textarea', required: false, placeholder: 'e.g. Top 40, R&B, classic hits, lounge during dinner...', mapTo: { target: 'music', field: 'musicStyle' } },
          { id: 'specialRequests', label: 'Other Info', type: 'textarea', rows: 6, required: false, placeholder: 'Anything else — extra details, requests, questions, or anything not covered above.', mapTo: { target: 'planning', field: 'specialRequests' } },
        ],
      },
    ],
  },
  party: {
    eventType: 'party',
    title: 'Party Planning Form',
    description: 'Help us make your party amazing! Share the details below.',
    steps: [
      {
        id: 'general',
        title: 'General Info',
        description: 'Basic event details',
        fields: generalInfoFields('Party'),
      },
      {
        id: 'highlights',
        title: 'Event Highlights',
        description: 'Key moments and entertainment',
        fields: [
          { id: 'partyTheme', label: 'Party Theme (if any)', type: 'text', required: false, placeholder: 'e.g. Tropical, 80s, Masquerade, Casino Night' },
          { id: 'cocktailMusic', label: 'Cocktail Music', type: 'yes_no', required: false, mapTo: { target: 'music', field: 'cocktailMusic' } },
          { id: 'cocktailMusicType', label: 'Cocktail Music Type', type: 'text', required: false, placeholder: 'e.g. Jazz, lounge, acoustic', conditional: { field: 'cocktailMusic', value: 'yes' }, mapTo: { target: 'music', field: 'cocktailMusicStyle' } },
          { id: 'dinnerMusic', label: 'Dinner Music', type: 'yes_no', required: false, mapTo: { target: 'music', field: 'dinnerMusic' } },
          { id: 'dinnerMusicType', label: 'Dinner Music Type', type: 'text', required: false, placeholder: 'e.g. Soft background, classic hits', conditional: { field: 'dinnerMusic', value: 'yes' }, mapTo: { target: 'music', field: 'dinnerMusicStyle' } },
          { id: 'speech', label: 'Speech', type: 'yes_no', required: false },
          { id: 'speechBy', label: 'Speech By', type: 'text', required: false, placeholder: 'Who is giving the speech?', conditional: { field: 'speech', value: 'yes' }, mapTo: { target: 'timeline', field: 'speechBy' } },
          { id: 'toast', label: 'Toast', type: 'yes_no', required: false },
          { id: 'toastBy', label: 'Toast By', type: 'text', required: false, placeholder: 'Who is giving the toast?', conditional: { field: 'toast', value: 'yes' }, mapTo: { target: 'timeline', field: 'toastBy' } },
          { id: 'whenToToast', label: 'When to Toast', type: 'text', required: false, placeholder: 'e.g. Before dinner, after cake', conditional: { field: 'toast', value: 'yes' }, mapTo: { target: 'timeline', field: 'toastTiming' } },
          { id: 'grandEntrance', label: 'Grand Entrance?', type: 'yes_no', required: false },
          { id: 'entranceSong', label: 'Entrance Song', type: 'song', required: false, placeholder: 'Song for the entrance', conditional: { field: 'grandEntrance', value: 'yes' }, mapTo: { target: 'music', field: 'entranceSong' } },
          { id: 'specialDances', label: 'Any special dances?', type: 'textarea', required: false, placeholder: 'First dance, group dance, etc.' },
          { id: 'specialMoments', label: 'Special Moments', type: 'textarea', required: false, placeholder: 'Cake cutting, surprise, etc.', mapTo: { target: 'timeline', field: 'specialMoments' } },
          { id: 'dinnerServed', label: 'Dinner Served', type: 'yes_no', required: false },
          { id: 'dinnerServedWhen', label: 'When is Dinner Served', type: 'text', required: false, placeholder: 'e.g. After cocktail hour', conditional: { field: 'dinnerServed', value: 'yes' }, mapTo: { target: 'timeline', field: 'dinnerTiming' } },
          { id: 'montage', label: 'Montage Viewing', type: 'yes_no', required: false },
          { id: 'montageTiming', label: 'Montage Viewing When', type: 'dropdown', required: false, options: ['Before Dinner', 'During Dinner', 'After Dinner', 'Before Dancing'], conditional: { field: 'montage', value: 'yes' } },
          { id: 'honorDance', label: 'Honor Dance', type: 'yes_no', required: false },
          { id: 'honorDanceSong', label: 'Honor Dance Song', type: 'song', required: false, placeholder: 'Song title — Artist', conditional: { field: 'honorDance', value: 'yes' }, mapTo: { target: 'music', field: 'honorDanceSong' } },
          { id: 'lastDance', label: 'Last Dance', type: 'yes_no', required: false },
          { id: 'lastDanceSong', label: 'Last Song', type: 'song', required: false, placeholder: 'Final song of the night', conditional: { field: 'lastDance', value: 'yes' }, mapTo: { target: 'music', field: 'lastDanceSong' } },
          { id: 'games', label: 'Game During Party?', type: 'yes_no', required: false },
          { id: 'gamesDetails', label: 'Game Details', type: 'textarea', required: false, placeholder: 'Describe any games, contests, or activities...', conditional: { field: 'games', value: 'yes' } },
          { id: 'dancersTshirts', label: 'Dancers T-Shirts?', type: 'yes_no', required: false },
          { id: 'dancersAttireColor', label: 'Dancers Attire Color', type: 'text', required: false, placeholder: 'e.g. Black, neon, match event colors', conditional: { field: 'dancersTshirts', value: 'yes' }, mapTo: { target: 'staffing', field: 'dancersAttireColor' } },
        ],
      },
      {
        id: 'introductions',
        title: 'Introductions',
        description: 'VIP and guest of honor introductions',
        fields: [
          { id: 'vipIntros', label: 'VIP or Guest of Honor Introductions?', type: 'yes_no', required: false },
          { id: 'introDetails', label: 'Who should be introduced?', type: 'textarea', required: false, placeholder: 'List in order...', conditional: { field: 'vipIntros', value: 'yes' }, mapTo: { target: 'timeline', field: 'familyIntros' } },
          { id: 'introSong', label: 'Introduction Song', type: 'song', required: false, placeholder: 'Background music for intros', conditional: { field: 'vipIntros', value: 'yes' } },
        ],
      },
      {
        id: 'other',
        title: 'Other Information',
        description: 'Music preferences and special requests',
        fields: otherInfoFields(),
      },
    ],
  },

  // ── ANNIVERSARY ──────────────────────────────────────────────────────
  anniversary: {
    eventType: 'anniversary',
    title: 'Anniversary Planning Form',
    description: 'Help us celebrate this special milestone! Share the details below.',
    steps: [
      {
        id: 'general',
        title: 'General Info',
        description: 'Event details',
        fields: [
          { id: 'coupleName', label: 'Couple Being Celebrated', type: 'text', required: true, critical: true, placeholder: 'e.g. John & Jane Smith', mapTo: { target: 'planning', field: 'guestOfHonor' } },
          { id: 'anniversaryYear', label: 'Anniversary Year (e.g. 25th, 50th)', type: 'text', required: false, placeholder: 'e.g. 50th' },
          { id: 'eventDate', label: 'Event Date', type: 'date', required: true, mapTo: { target: 'planning', field: 'eventDate' } },
          { id: 'startTime', label: 'Start Time', type: 'time', required: true, mapTo: { target: 'logistics', field: 'eventStartTime' } },
          { id: 'endTime', label: 'End Time', type: 'time', required: true, mapTo: { target: 'logistics', field: 'eventEndTime' } },
          { id: 'venueName', label: 'Venue Name', type: 'text', required: true, placeholder: 'Venue name', mapTo: { target: 'logistics', field: 'venueName' } },
          { id: 'guestCount', label: 'Expected Guests', type: 'number', required: true, placeholder: '0', mapTo: { target: 'planning', field: 'adultCount' } },
          { id: 'contactName', label: 'Event Coordinator', type: 'text', required: true, placeholder: 'Who should we coordinate with?' },
          { id: 'contactPhone', label: 'Contact Phone', type: 'phone', required: true, placeholder: '(555) 123-4567' },
        ],
      },
      {
        id: 'reception',
        title: 'Reception Highlights',
        description: 'Key moments of the celebration',
        fields: [
          { id: 'cocktailMusic', label: 'Cocktail Music', type: 'yes_no', required: false, mapTo: { target: 'music', field: 'cocktailMusic' } },
          { id: 'cocktailMusicType', label: 'Cocktail Music Type', type: 'text', required: false, placeholder: 'e.g. Jazz, lounge, acoustic', conditional: { field: 'cocktailMusic', value: 'yes' }, mapTo: { target: 'music', field: 'cocktailMusicStyle' } },
          { id: 'dinnerMusic', label: 'Dinner Music', type: 'yes_no', required: false, mapTo: { target: 'music', field: 'dinnerMusic' } },
          { id: 'dinnerMusicType', label: 'Dinner Music Type', type: 'text', required: false, placeholder: 'e.g. Soft background, classic hits', conditional: { field: 'dinnerMusic', value: 'yes' }, mapTo: { target: 'music', field: 'dinnerMusicStyle' } },
          { id: 'coupleEntrance', label: 'Couple Entrance?', type: 'yes_no', required: false },
          { id: 'entranceSong', label: 'Entrance Song', type: 'song', required: false, placeholder: 'Song for the couple entrance', conditional: { field: 'coupleEntrance', value: 'yes' }, mapTo: { target: 'music', field: 'entranceSong' } },
          { id: 'anniversaryDance', label: 'Anniversary Dance?', type: 'yes_no', required: false },
          { id: 'anniversaryDanceSong', label: 'Anniversary Dance Song', type: 'song', required: false, placeholder: 'Their song or original wedding song', conditional: { field: 'anniversaryDance', value: 'yes' }, mapTo: { target: 'music', field: 'firstDanceSong' } },
          { id: 'originalWeddingSong', label: 'Original Wedding Song (if different)', type: 'song', required: false, placeholder: 'The song from their wedding day' },
          { id: 'toast', label: 'Toast / Speech', type: 'yes_no', required: false },
          { id: 'toastBy', label: 'Toast By', type: 'text', required: false, placeholder: 'Children, friends, family...', conditional: { field: 'toast', value: 'yes' }, mapTo: { target: 'timeline', field: 'toastBy' } },
          { id: 'whenToToast', label: 'When to Toast', type: 'text', required: false, placeholder: 'e.g. Before dinner, after dessert', conditional: { field: 'toast', value: 'yes' }, mapTo: { target: 'timeline', field: 'toastTiming' } },
          { id: 'cakeCutting', label: 'Cake Cutting?', type: 'yes_no', required: false },
          { id: 'cakeSong', label: 'Cake Cutting Song', type: 'song', required: false, placeholder: 'Song title — Artist', conditional: { field: 'cakeCutting', value: 'yes' }, mapTo: { target: 'music', field: 'cakeSong' } },
          { id: 'dinnerServed', label: 'Dinner Served', type: 'yes_no', required: false },
          { id: 'dinnerServedWhen', label: 'When is Dinner Served', type: 'text', required: false, placeholder: 'e.g. After cocktail hour', conditional: { field: 'dinnerServed', value: 'yes' }, mapTo: { target: 'timeline', field: 'dinnerTiming' } },
          { id: 'slideshow', label: 'Photo Slideshow / Montage?', type: 'yes_no', required: false },
          { id: 'slideshowTiming', label: 'Slideshow When', type: 'dropdown', required: false, options: ['Before Dinner', 'During Dinner', 'After Dinner', 'Before Dancing'], conditional: { field: 'slideshow', value: 'yes' } },
          { id: 'honorDance', label: 'Honor Dance', type: 'yes_no', required: false },
          { id: 'honorDanceSong', label: 'Honor Dance Song', type: 'song', required: false, placeholder: 'Song title — Artist', conditional: { field: 'honorDance', value: 'yes' }, mapTo: { target: 'music', field: 'honorDanceSong' } },
          { id: 'specialDances', label: 'Any other special dances?', type: 'textarea', required: false, placeholder: 'Family dance, group dance, etc.' },
          { id: 'lastDance', label: 'Last Dance', type: 'yes_no', required: false },
          { id: 'lastDanceSong', label: 'Last Song', type: 'song', required: false, placeholder: 'Final song of the night', conditional: { field: 'lastDance', value: 'yes' }, mapTo: { target: 'music', field: 'lastDanceSong' } },
        ],
      },
      {
        id: 'introductions',
        title: 'Introductions',
        description: 'Family and VIP introductions',
        fields: [
          { id: 'familyIntros', label: 'Family Introductions?', type: 'yes_no', required: false },
          { id: 'familyIntroDetails', label: 'Who should be introduced?', type: 'textarea', required: false, placeholder: 'Children, grandchildren, family...', conditional: { field: 'familyIntros', value: 'yes' }, mapTo: { target: 'timeline', field: 'familyIntros' } },
          { id: 'introSong', label: 'Introduction Song', type: 'song', required: false, placeholder: 'Background music for intros', conditional: { field: 'familyIntros', value: 'yes' } },
        ],
      },
      {
        id: 'other',
        title: 'Other Information',
        description: 'Music preferences and special requests',
        fields: otherInfoFields(),
      },
    ],
  },
};

// ── Public API ─────────────────────────────────────────────────────────

/**
 * THE routing table. ONE ordered list, first match wins.
 *
 * ROOT CAUSE this replaces: nine schemas were built and tested, and the
 * resolver named only four of them. `corporate`, `school`, `party` and
 * `anniversary` were fully written, covered by tests, and unreachable —
 * nothing was broken, they were simply never named, so four real live events
 * were handed the generic form.
 *
 * ORDER IS THE WHOLE FIX. Real event names carry two of these words at once:
 *   • "Wedding Anniversary" is an ANNIVERSARY  → anniversary precedes wedding,
 *     and a plain wedding never contains the word anniversary.
 *   • "Corporate Holiday Party" is CORPORATE and "Graduation Party" is SCHOOL
 *     → both precede party. "Holiday Party" with no corporate word is a party.
 *   • "Graduation" goes to SCHOOL deliberately: that schema asks for School
 *     Name, Student Arrival Time, Expected Students and Staff Contact, which is
 *     exactly what a graduation needs.
 * Adding a keyword to a list further down can never steal a match from one
 * above it, which is why this is a table and not a chain of ifs.
 */
export const PLANNING_SCHEMA_ROUTES = [
  { schemaKey: 'anniversary', keywords: ['anniversary'] },
  { schemaKey: 'mitzvah', keywords: ['mitzvah', 'bar mitzvah', 'bat mitzvah', "b'nai"] },
  { schemaKey: 'wedding', keywords: ['wedding', 'nuptial', 'reception', 'ceremony'] },
  { schemaKey: 'sweet16', keywords: ['sweet 16', 'sweet sixteen', 'sweet16'] },
  { schemaKey: 'quinceanera', keywords: ['quince', 'quinceanera', 'quinceañera'] },
  { schemaKey: 'corporate', keywords: [
    'corporate', 'company', 'conference', 'gala', 'awards', 'product launch',
    'team building', 'office', 'client appreciation', 'fundraiser', 'nonprofit',
    // NOTE: "holiday party" is deliberately NOT here. A COMPANY holiday party
    // already matches on 'corporate' / 'company' / 'office', while a plain
    // "Holiday Party" is somebody's house at Christmas — it belongs to `party`
    // below, which is where 'holiday' lives.
  ] },
  { schemaKey: 'school', keywords: [
    'school', 'prom', 'homecoming', 'graduation', 'grad', 'senior', 'pep rally',
    'college', 'university', 'alumni', 'sorority', 'fraternity',
  ] },
  { schemaKey: 'party', keywords: [
    'party', 'birthday', 'engagement', 'retirement', 'reunion', 'baby shower',
    'bridal shower', 'housewarming', 'holiday',
  ] },
];

/** The form every unmatched event type gets, on purpose. See below. */
export const PLANNING_FALLBACK_SCHEMA_KEY = 'essentials';

// Accent-insensitive, punctuation-insensitive, spaces KEPT so keywords can be
// matched on word boundaries: "grad" must not fire on "upgrade".
const normalizeEventType = (value) => String(value)
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, ' ')
  .trim();

const keywordHits = (haystack, keyword) => {
  const needle = normalizeEventType(keyword);
  if (!needle) return false;
  const pattern = needle.split(' ').map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('\\s+');
  return new RegExp(`\\b${pattern}\\b`).test(haystack);
};

/**
 * THE ONE decision: which planning schema key does this event use?
 *
 * @param eventType the event's type as typed by the admin ("Bar Mitzvah",
 *        "Corporate Holiday Party", "Event", ...)
 * @param options.recordedSchemaKey the schema key ALREADY recorded against this
 *        event's saved planning answers, if any.
 *
 * LIVE-DATA SAFETY — why the recorded key wins:
 *   An event that is already being planned must not have its form swapped
 *   underneath it. The key resolved at the time answers were first saved is
 *   stored on the planning submission, and it is authoritative from then on.
 *   Every event with answers saved before this routing existed carries
 *   'essentials' there, so THOSE EVENTS KEEP ESSENTIALS — the shape their
 *   answers were captured in. That is deliberate, not an oversight, and it is
 *   why nothing is written or backfilled onto any existing event.
 *   Absent and empty are different: only the absence of a recorded key
 *   resolves fresh from the type. An event with no saved answers therefore
 *   picks up the new routing immediately, which is the desired behaviour.
 *
 * A missing, empty or non-string type returns the fallback without throwing.
 */
export const resolvePlanningSchemaKey = (eventType, options = {}) => {
  const recorded = options && options.recordedSchemaKey;
  if (typeof recorded === 'string' && recorded && planningSchemas[recorded]) {
    return recorded;
  }
  if (!eventType || typeof eventType !== 'string') return PLANNING_FALLBACK_SCHEMA_KEY;
  const normalized = normalizeEventType(eventType);
  if (!normalized) return PLANNING_FALLBACK_SCHEMA_KEY;
  // A bare schema key resolves to itself, whatever its casing. Consumers hand
  // this function the recorded key through this same argument
  // (`planningContent.eventType` IS a schema key), so "essentials" must mean
  // essentials and legacy "songRequest" must mean songRequest.
  const collapsed = normalized.replace(/ /g, '');
  const exact = Object.keys(planningSchemas).find((k) => k.toLowerCase() === collapsed);
  if (exact) return exact;
  const hit = PLANNING_SCHEMA_ROUTES.find(
    (route) => route.keywords.some((k) => keywordHits(normalized, k)),
  );
  // NO MATCH IS A REAL ANSWER. Eight live events are typed simply "Event": a
  // generic type gets the generic form, and it is never guessed at from the
  // client's name, the venue or the guest count.
  return hit ? hit.schemaKey : PLANNING_FALLBACK_SCHEMA_KEY;
};

/** Get schema for an event type. Unmatched types get `essentials`. */
export const getPlanningSchema = (eventType, options = {}) => (
  planningSchemas[resolvePlanningSchemaKey(eventType, options)]
);

/**
 * Schema keys that EXIST in this file but are NOT event types a person picks.
 * One function was being asked two different questions — "what schemas exist"
 * and "what can be chosen" — and answered the first for both. They are not the
 * same list, so the exclusions are declared here with the reason for each.
 */
export const NON_SELECTABLE_SCHEMA_KEYS = {
  // Retired music-only form. Kept so old records that captured answers in it
  // still render, never offered for a new event.
  songRequest: 'retired music-only form, kept for existing records',
  // The generic form served when an event type matches no route. Reached
  // through PLANNING_FALLBACK_SCHEMA_KEY, never chosen from a list.
  [PLANNING_FALLBACK_SCHEMA_KEY]: 'generic fallback form, not an event type',
};

/**
 * The event types an admin can actually choose.
 *
 * DERIVED, deliberately: no count and no name is written here, so adding a
 * tenth real schema tomorrow makes this ten on its own. If you need the whole
 * map including the non-selectable forms, read `planningSchemas` directly.
 */
export const getSupportedEventTypes = () => Object.keys(planningSchemas)
  .filter((key) => !(key in NON_SELECTABLE_SCHEMA_KEYS));

/** Get all fields from a schema (flattened) */
export const getAllSchemaFields = (schema) => {
  if (!schema?.steps) return [];
  return schema.steps.flatMap(s => s.fields || []);
};


// ── Conditional Logic Engine ──────────────────────────────────────────
//
// Supports three condition shapes on any field:
//
//   showIf:    { field, value }           → single condition
//   showIfAll: [{ field, value }, ...]    → ALL must match (AND)
//   showIfAny: [{ field, value }, ...]    → ANY must match (OR)
//
// Backward compatible: legacy `conditional: { field, value }` still works.
//
// Hidden fields are preserved in answers but excluded from:
//   - active validation (required checks skip hidden fields)
//   - mapping layer (getVisibleAnswers filters them out)
//   - risk/health scoring (getMissingCriticalFields skips them)
// ──────────────────────────────────────────────────────────────────────

/**
 * Evaluate a single condition: { field, value } or { field, value, op }.
 * Default comparison is strict equality.
 * Supports operators: 'eq' (default), 'neq', 'in', 'notEmpty'.
 */
export const evaluateCondition = (condition, answers) => {
  if (!condition || !condition.field) return true;
  const actual = answers[condition.field];
  const op = condition.op || 'eq';

  switch (op) {
    case 'neq':
      return actual !== condition.value;
    case 'in':
      return Array.isArray(condition.value) && condition.value.includes(actual);
    case 'notEmpty':
      return actual !== undefined && actual !== null && actual !== '' &&
             (typeof actual !== 'string' || actual.trim() !== '');
    case 'eq':
    default:
      return actual === condition.value;
  }
};

/**
 * Check if a field should be visible given current answers.
 *
 * Priority: showIfAll > showIfAny > showIf > conditional (legacy)
 * If none are present, the field is always visible.
 */
export const isFieldVisible = (field, answers) => {
  if (!field) return false;
  if (!answers) return !field.showIf && !field.showIfAll && !field.showIfAny && !field.conditional;

  // showIfAll — every condition must pass (AND)
  if (field.showIfAll && Array.isArray(field.showIfAll)) {
    return field.showIfAll.every(c => evaluateCondition(c, answers));
  }

  // showIfAny — at least one condition must pass (OR)
  if (field.showIfAny && Array.isArray(field.showIfAny)) {
    return field.showIfAny.some(c => evaluateCondition(c, answers));
  }

  // showIf — single condition (new canonical form)
  if (field.showIf) {
    return evaluateCondition(field.showIf, answers);
  }

  // conditional — legacy backward-compat (same as showIf)
  if (field.conditional) {
    return evaluateCondition(field.conditional, answers);
  }

  return true;
};

/**
 * Get the set of currently visible field IDs for a schema + answers.
 */
export const getVisibleFieldIds = (schema, answers) => {
  if (!schema?.steps || !answers) return new Set();
  const ids = new Set();
  for (const step of schema.steps) {
    for (const field of step.fields || []) {
      if (isFieldVisible(field, answers)) {
        ids.add(field.id);
      }
    }
  }
  return ids;
};

/**
 * Return a filtered copy of answers containing only values for currently visible fields.
 * Hidden field values are preserved in the original but excluded from the output.
 * Use this before mapping/validation to respect conditional visibility.
 */
export const getVisibleAnswers = (schema, answers) => {
  if (!schema?.steps || !answers) return {};
  const visibleIds = getVisibleFieldIds(schema, answers);
  const filtered = {};
  for (const [key, val] of Object.entries(answers)) {
    if (visibleIds.has(key)) {
      filtered[key] = val;
    }
  }
  return filtered;
};

/** Compute completion stats for a schema given answers */
export const computeCompletion = (schema, answers) => {
  if (!schema?.steps || !answers) return { total: 0, filled: 0, required: 0, requiredFilled: 0, percent: 0 };
  const allFields = getAllSchemaFields(schema);
  const visibleFields = allFields.filter(f => isFieldVisible(f, answers));
  const requiredFields = visibleFields.filter(f => f.required);

  const isFilled = (f) => {
    const v = answers[f.id];
    if (v === undefined || v === null || v === '') return false;
    if (typeof v === 'string' && !v.trim()) return false;
    return true;
  };

  const filled = visibleFields.filter(isFilled).length;
  const requiredFilled = requiredFields.filter(isFilled).length;

  return {
    total: visibleFields.length,
    filled,
    required: requiredFields.length,
    requiredFilled,
    percent: visibleFields.length > 0 ? Math.round((filled / visibleFields.length) * 100) : 0,
    allRequiredComplete: requiredFilled === requiredFields.length,
  };
};


/**
 * Dependent Cascade: Get child field IDs that depend on a given parent field.
 * When a parent yes_no field changes to 'no', these children should be cleared.
 *
 * Checks all condition shapes: showIf, showIfAll, showIfAny, conditional.
 */
export const getDependentFieldIds = (schema, parentFieldId) => {
  if (!schema?.steps) return [];
  const deps = [];
  for (const step of schema.steps) {
    for (const field of step.fields || []) {
      const refs = [];
      if (field.conditional?.field) refs.push(field.conditional.field);
      if (field.showIf?.field) refs.push(field.showIf.field);
      if (field.showIfAll) field.showIfAll.forEach(c => { if (c.field) refs.push(c.field); });
      if (field.showIfAny) field.showIfAny.forEach(c => { if (c.field) refs.push(c.field); });
      if (refs.includes(parentFieldId)) {
        deps.push(field.id);
      }
    }
  }
  return deps;
};

/**
 * Apply dependent cascade: given a changed field and its new value,
 * return a new answers object with hidden-by-cascade children cleared.
 *
 * Only clears children that would become invisible after the change.
 * Preserves the parent's new value. Recursive for nested dependencies.
 */
export const applyCascadeClear = (schema, answers, changedFieldId, newValue) => {
  const next = { ...answers, [changedFieldId]: newValue };
  const deps = getDependentFieldIds(schema, changedFieldId);

  for (const depId of deps) {
    // Find the field definition
    const depField = getAllSchemaFields(schema).find(f => f.id === depId);
    if (!depField) continue;
    // Check if this dependent field is now hidden
    if (!isFieldVisible(depField, next)) {
      // Clear its value
      delete next[depId];
      // Recursively cascade (the cleared dep might have its own children)
      const grandDeps = getDependentFieldIds(schema, depId);
      for (const gd of grandDeps) {
        const gdField = getAllSchemaFields(schema).find(f => f.id === gd);
        if (gdField && !isFieldVisible(gdField, next)) {
          delete next[gd];
        }
      }
    }
  }

  return next;
};

export default planningSchemas;
